import React, { useState } from 'react';
import { useBackgroundRemoval } from '../../hooks/useBackgroundRemoval';
import InlineCropper from '../InlineCropper';

function GamePreviewRecapSharedFields({
  formData,
  templateType,
  template,
  graphicsLimitInfo,
  isOrgTeam,
  awayTeamLogoPreview,
  saving,
  downloading,
  onChange,
  onAwayLogoChange,
  onRemoveAwayLogo,
  onSetAwayLogoUrl,
  onDateFormatClick,
  hideGameDate = false,
}) {
  const [originalAwayLogo, setOriginalAwayLogo] = useState(null);
  const [cutoutAwayLogo, setCutoutAwayLogo] = useState(null);
  const [activeAwayVersion, setActiveAwayVersion] = useState('original');
  const [awayCropping, setAwayCropping] = useState(false);
  const [awayTempImage, setAwayTempImage] = useState(null);

  const { removeBg: removeAwayBg, isRemoving: isRemovingAway, bgRemovalError: awayRemovalError } = useBackgroundRemoval();

  const handleAwayLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setAwayTempImage(reader.result);
      setAwayCropping(true);
    };
    reader.readAsDataURL(file);
  };

  const handleAwayCropComplete = (croppedImage) => {
    setOriginalAwayLogo(croppedImage);
    setCutoutAwayLogo(null);
    setActiveAwayVersion('original');
    onSetAwayLogoUrl(croppedImage);
    setAwayCropping(false);
    setAwayTempImage(null);
    const input = document.getElementById('away-team-logo');
    if (input) input.value = '';
  };

  const handleAwayCropCancel = () => {
    setAwayCropping(false);
    setAwayTempImage(null);
    const input = document.getElementById('away-team-logo');
    if (input) input.value = '';
  };

  const handleEditAwayCrop = () => {
    if (originalAwayLogo) {
      setAwayTempImage(originalAwayLogo);
      setAwayCropping(true);
    }
  };

  const handleRemoveAwayBg = async () => {
    if (!originalAwayLogo && !awayTeamLogoPreview) return;
    const source = originalAwayLogo || awayTeamLogoPreview;
    const result = await removeAwayBg(source);
    if (result) {
      const { blobUrl, toBase64 } = result;
      setCutoutAwayLogo(blobUrl);
      setActiveAwayVersion('cutout');
      onSetAwayLogoUrl(blobUrl);
      toBase64.then((base64) => {
        setCutoutAwayLogo(base64);
        onSetAwayLogoUrl(base64);
      });
    }
  };

  const handleToggleAwayVersion = (version) => {
    setActiveAwayVersion(version);
    onSetAwayLogoUrl(version === 'cutout' ? cutoutAwayLogo : originalAwayLogo);
  };

  const handleRemoveAwayLogo = () => {
    setOriginalAwayLogo(null);
    setCutoutAwayLogo(null);
    setActiveAwayVersion('original');
    onRemoveAwayLogo();
  };
  return (
    <>
      <div className="form-group">
        <label htmlFor="homeTeam">
          Team Name
          <span
            style={{
              fontSize: '12px',
              color: '#718096',
              marginLeft: '8px',
            }}
          >
            ({formData.homeTeam.length}/25)
          </span>
        </label>
        <input
          id="homeTeam"
          name="homeTeam"
          type="text"
          value={formData.homeTeam}
          onChange={onChange}
          placeholder="Your Team"
          maxLength={25}
        />
      </div>

      <div className="form-group">
        <label htmlFor="awayTeam">
          Opponent Team *
          <span
            style={{
              fontSize: '12px',
              color: '#718096',
              marginLeft: '8px',
            }}
          >
            ({formData.awayTeam.length}/25)
          </span>
        </label>
        <input
          id="awayTeam"
          name="awayTeam"
          type="text"
          value={formData.awayTeam}
          onChange={onChange}
          placeholder="Opponent Name"
          required
          maxLength={25}
        />
      </div>

      {/* AWAY TEAM LOGO SECTION - All templates */}
      {graphicsLimitInfo &&
        (isOrgTeam || graphicsLimitInfo.subscriptionPlan !== 'free') && (
          <div className="form-group">
            <label>Opponent Logo (Optional)</label>
            {awayCropping ? (
              <InlineCropper
                imageSrc={awayTempImage}
                onCropComplete={handleAwayCropComplete}
                onCancel={handleAwayCropCancel}
                aspectRatio={1}
              />
            ) : awayTeamLogoPreview ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
              <div className="opponent-logo-preview-container">
                <img
                  src={awayTeamLogoPreview}
                  alt="Opponent logo"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
                {isRemovingAway && (
                  <div style={{
                    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10,
                  }}>
                    <div style={{
                      width: '28px', height: '28px',
                      border: '3px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff', borderRadius: '50%',
                      animation: 'bg-removal-spin 0.75s linear infinite',
                    }} />
                    <p style={{ color: '#fff', fontSize: '12px', margin: '8px 0 0' }}>Removing…</p>
                  </div>
                )}
                <button
                  type="button"
                  className="btn-edit-photo-overlay"
                  onClick={handleEditAwayCrop}
                  disabled={saving || downloading || isRemovingAway}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn-remove-background-photo"
                  onClick={handleRemoveAwayLogo}
                  disabled={saving || downloading || isRemovingAway}
                >
                  ✕ Remove
                </button>
              </div>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleRemoveAwayBg}
                disabled={isRemovingAway || saving || downloading}
                style={{ marginTop: '8px', width: '100%' }}
              >
                {isRemovingAway ? 'Removing background…' : '✂️ Remove Background'}
              </button>
              {awayRemovalError && (
                <p style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>{awayRemovalError}</p>
              )}
                {cutoutAwayLogo && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button
                      type="button"
                      className={activeAwayVersion === 'original' ? 'btn-primary' : 'btn-secondary'}
                      onClick={() => handleToggleAwayVersion('original')}
                      style={{ flex: 1 }}
                      disabled={isRemovingAway}
                    >
                      Original
                    </button>
                    <button
                      type="button"
                      className={activeAwayVersion === 'cutout' ? 'btn-primary' : 'btn-secondary'}
                      onClick={() => handleToggleAwayVersion('cutout')}
                      style={{ flex: 1 }}
                      disabled={isRemovingAway}
                    >
                      ✂️ Cutout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <label htmlFor="away-team-logo" className="away-logo-upload-btn">
                <span>Upload Opponent Logo</span>
                <input
                  id="away-team-logo"
                  type="file"
                  accept="image/*"
                  onChange={handleAwayLogoChange}
                  style={{ display: 'none' }}
                  disabled={saving || downloading}
                />
              </label>
            )}
          </div>
        )}

      {!hideGameDate && (
        <div className="form-group">
          <label htmlFor="gameDate">Game Date *</label>
          <input
            id="gameDate"
            name="gameDate"
            type="date"
            value={formData.gameDate}
            onChange={onChange}
            placeholder="Select date"
            required
          />
          <button
            type="button"
            className="format-link"
            onClick={onDateFormatClick}
            style={{
              background: 'none',
              border: 'none',
              color: '#667eea',
              fontSize: '13px',
              cursor: 'pointer',
              marginTop: '4px',
              padding: '0',
              textDecoration: 'underline',
            }}
          >
            Change date format
          </button>
        </div>
      )}
      {templateType === 'preview' && (
        <>
          <div className="form-group">
            <label htmlFor="gameTime">Game Time *</label>
            <input
              id="gameTime"
              name="gameTime"
              type="time"
              value={formData.gameTime}
              onChange={onChange}
              placeholder="Select time"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="location">
              Location
              <span
                style={{
                  fontSize: '12px',
                  color: '#718096',
                  marginLeft: '8px',
                }}
              >
                ({formData.location.length}/50)
              </span>
            </label>
            <input
              id="location"
              name="location"
              type="text"
              value={formData.location}
              onChange={onChange}
              placeholder="Enter location"
              maxLength={50}
            />
          </div>
        </>
      )}
    </>
  );
}

export default GamePreviewRecapSharedFields;
